import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { type Session, ROLE_LABELS, api, generatePassword } from "../api";
import { Button, Card, ErrorNote, Loading, Pill } from "../components/Ui";
import { ago } from "../format";
import { useApi } from "../useApi";

/**
 * Felhasználók kezelése.
 *
 * **Törlés nincs, letiltás van.** A napló hivatkozik a felhasználóra (ki
 * aktivált szabályt, ki javított feldolgozást) — egy törölt fiók nyomai
 * elárvulnának. A letiltott fiók minden munkamenete azonnal megszűnik.
 *
 * A saját fiók letiltását és szerepkörének módosítását a felület sem
 * kínálja fel: a szerver elutasítaná, és egy elkattintás után senki nem
 * tudná visszaállítani.
 */

export interface AdminUserRow {
  id: number;
  email: string;
  display_name: string;
  role: string;
  active: boolean;
  locked: boolean;
  last_login_at: string | null;
  created_at: string | null;
}

const ROLES = ["adminisztrator", "munkatars", "olvaso"] as const;

export function UsersView({ session }: { session: Session }): JSX.Element {
  const { data, error, loading, reload } = useApi<AdminUserRow[]>("/admin/v1/users");
  const [message, setMessage] = useState<{ tone: string; text: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);

  const done = (text: string): void => {
    setMessage({ tone: "info", text });
    setEditing(null);
    setCreating(false);
    reload();
  };
  const failed = (e: unknown): void =>
    setMessage({ tone: "bad", text: e instanceof Error ? e.message : String(e) });

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;
  if (!data) return <></>;

  return (
    <>
      {message && <p className={`notice notice--${message.tone}`}>{message.text}</p>}

      <Card
        title="Felhasználók"
        hint="Ki léphet be az adminba, és mit tehet. Letiltás után a munkamenet azonnal megszűnik."
        actions={
          !creating && (
            <Button tone="primary" onClick={() => setCreating(true)}>
              Új felhasználó
            </Button>
          )
        }
      >
        {creating && (
          <CreateForm
            onCancel={() => setCreating(false)}
            onCreated={(email) => done(`${email} létrehozva.`)}
            onError={failed}
          />
        )}

        <table className="rows">
          <thead>
            <tr>
              <th>Név / e-mail</th>
              <th>Szerepkör</th>
              <th>Állapot</th>
              <th>Utolsó belépés</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                self={user.email === session.email}
                editing={editing === user.id}
                onEdit={() => setEditing(editing === user.id ? null : user.id)}
                onDone={done}
                onError={failed}
              />
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function UserRow({
  user,
  self,
  editing,
  onEdit,
  onDone,
  onError,
}: {
  user: AdminUserRow;
  self: boolean;
  editing: boolean;
  onEdit: () => void;
  onDone: (text: string) => void;
  onError: (e: unknown) => void;
}): JSX.Element {
  return (
    <>
      <tr className={user.active ? "" : "is-dim"}>
        <td>
          <strong>{user.display_name || user.email}</strong>
          {self && <Pill tone="info">Ön</Pill>}
          <br />
          <span className="muted">{user.email}</span>
        </td>
        <td>{ROLE_LABELS[user.role] ?? user.role}</td>
        <td>
          {user.active ? <Pill tone="ok">aktív</Pill> : <Pill tone="muted">letiltva</Pill>}
          {user.locked && <Pill tone="warn">zárolva</Pill>}
        </td>
        <td className="muted">{user.last_login_at ? ago(user.last_login_at) : "még nem lépett be"}</td>
        <td className="num">
          <Button tone="ghost" onClick={onEdit}>
            {editing ? "Bezárás" : "Kezelés"}
          </Button>
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={5}>
            <EditPanel user={user} self={self} onDone={onDone} onError={onError} />
          </td>
        </tr>
      )}
    </>
  );
}

function CreateForm({
  onCancel,
  onCreated,
  onError,
}: {
  onCancel: () => void;
  onCreated: (email: string) => void;
  onError: (e: unknown) => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<string>("munkatars");
  const [password, setPassword] = useState(generatePassword());
  const [busy, setBusy] = useState(false);

  const submit = async (event: Event): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/v1/users", { email, display_name: name, role, password });
      onCreated(email.trim().toLowerCase());
    } catch (e: unknown) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form" onSubmit={submit}>
      <label>
        E-mail cím
        <input
          type="email"
          required
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
        />
      </label>
      <label>
        Név
        <input value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
      </label>
      <label>
        Szerepkör
        <RoleSelect value={role} onChange={setRole} />
      </label>
      <PasswordField value={password} onChange={setPassword} />
      <p className="muted form__wide">
        A kezdőjelszót adja át a munkatársnak — belépés után a „Fiókom” oldalon
        megváltoztathatja.
      </p>
      <div className="form__actions">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? "Mentés…" : "Létrehozás"}
        </button>
        <Button tone="ghost" onClick={onCancel}>
          Mégse
        </Button>
      </div>
    </form>
  );
}

function EditPanel({
  user,
  self,
  onDone,
  onError,
}: {
  user: AdminUserRow;
  self: boolean;
  onDone: (text: string) => void;
  onError: (e: unknown) => void;
}): JSX.Element {
  const [name, setName] = useState(user.display_name);
  const [role, setRole] = useState(user.role);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<unknown>, text: string): Promise<void> => {
    setBusy(true);
    try {
      await action();
      onDone(text);
    } catch (e: unknown) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const save = (): Promise<void> =>
    run(
      () =>
        api.patch(`/admin/v1/users/${user.id}`, {
          display_name: name,
          ...(self ? {} : { role }),
        }),
      `${user.email} mentve.${!self && role !== user.role ? " Az új jogokkal újra be kell lépnie." : ""}`,
    );

  return (
    <div className="panel">
      <div className="form">
        <label>
          Név
          <input value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        </label>
        <label>
          Szerepkör
          {self ? (
            <span className="muted">{ROLE_LABELS[user.role]} — a sajátját nem módosíthatja</span>
          ) : (
            <RoleSelect value={role} onChange={setRole} />
          )}
        </label>
        <div className="form__actions">
          <Button tone="primary" onClick={() => void save()} disabled={busy}>
            Mentés
          </Button>
        </div>
      </div>

      <div className="form">
        <PasswordField value={password} onChange={setPassword} label="Új jelszó" />
        <div className="form__actions">
          <Button
            onClick={() =>
              void run(
                () => api.post(`/admin/v1/users/${user.id}/password`, { password }),
                `${user.email} jelszava beállítva. A korábbi munkamenetei megszűntek.`,
              )
            }
            disabled={busy || password.length < 10}
          >
            Jelszó beállítása
          </Button>
        </div>
      </div>

      <div className="form__actions">
        {user.locked && (
          <Button
            onClick={() =>
              void run(
                () => api.post(`/admin/v1/users/${user.id}/unlock`),
                `${user.email} zárolása feloldva.`,
              )
            }
            disabled={busy}
          >
            Zárolás feloldása
          </Button>
        )}
        {!self &&
          (user.active ? (
            <Button
              onClick={() =>
                void run(
                  () => api.patch(`/admin/v1/users/${user.id}`, { active: false }),
                  `${user.email} letiltva. A munkamenete azonnal megszűnt.`,
                )
              }
              disabled={busy}
            >
              Letiltás
            </Button>
          ) : (
            <Button
              onClick={() =>
                void run(
                  () => api.patch(`/admin/v1/users/${user.id}`, { active: true }),
                  `${user.email} újra aktív.`,
                )
              }
              disabled={busy}
            >
              Újraengedélyezés
            </Button>
          ))}
      </div>
    </div>
  );
}

function RoleSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <select value={value} onChange={(e) => onChange((e.target as HTMLSelectElement).value)}>
      {ROLES.map((role) => (
        <option key={role} value={role}>
          {ROLE_LABELS[role]}
        </option>
      ))}
    </select>
  );
}

export function PasswordField({
  value,
  onChange,
  label = "Kezdőjelszó",
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}): JSX.Element {
  return (
    <label>
      {label}
      <span className="form__inline">
        <input
          type="text"
          autoComplete="new-password"
          minLength={10}
          required
          value={value}
          onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        />
        <Button tone="ghost" onClick={() => onChange(generatePassword())}>
          Generálás
        </Button>
      </span>
      <small className="muted">Legalább 10 karakter.</small>
    </label>
  );
}
